"use client";

import { clarityEvent, clarityIdentify, claritySet } from "./clarity.service";
import {
  getDistinctId,
  registerSuperProperties,
  trackEvent,
  unregisterSuperProperties,
} from "./mixpanel.service";

type AnalyticsUser = { id: string; email?: string | null; user_type?: string | null } | null;

let currentAnalyticsUser: AnalyticsUser = null;

export function setAnalyticsUser(user: AnalyticsUser): void {
  currentAnalyticsUser = user;
}

function getCurrentUserId(): string | null {
  return currentAnalyticsUser?.id ?? null;
}

function currentPathname(): string {
  if (typeof window === "undefined") return "/";
  return window.location.pathname || "/";
}

const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_MICROSOFT_CLARITY ?? "";

const CLARITY_SESSION_KEY = "clarity_custom_session_id";

const CLARITY_SUPER_PROPS = [
  "clarity_session_id",
  "clarity_custom_user_id",
  "clarity_visitor_type",
] as const;

// Per-event URL window centered on the event's UTC timestamp.
// 5 min before: generous enough for minor clock skew between client and Clarity's recording chunks.
// 2 min after: keeps the Clarity time slider close to the moment of the event.
const WINDOW_BEFORE_MS = 5 * 60 * 1000;
const WINDOW_AFTER_MS = 2 * 60 * 1000;

function getOrCreateClaritySessionId(): string {
  try {
    const existing = sessionStorage.getItem(CLARITY_SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(CLARITY_SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

// Takes explicit start/end ms so each call site controls the window.
function buildRecordingUrl(customUserId: string, startMs: number, endMs: number): string {
  if (!CLARITY_PROJECT_ID || !customUserId) return "";
  return `https://clarity.microsoft.com/projects/view/${CLARITY_PROJECT_ID}/impressions?CustomUserId=is%3B${encodeURIComponent(customUserId)}&date=Custom&end=${endMs}&start=${startMs}`;
}

// Called when the user IS authenticated — stamps identity super-props synchronously
// so every subsequent event carries the correct clarity_custom_user_id.
// Clarity identify fires in the background and doesn't block anything.
export function syncClarityIntoMixpanel(userId: string, pathname: string): void {
  const distinctId = getDistinctId() ?? userId;
  const sessionId = getOrCreateClaritySessionId();

  unregisterSuperProperties(...CLARITY_SUPER_PROPS);

  registerSuperProperties({
    clarity_session_id: sessionId,
    clarity_custom_user_id: userId,
    clarity_visitor_type: "authenticated",
  });

  claritySet("mp_distinct_id", distinctId);
  clarityIdentify(userId, sessionId, pathname);
}

// Called when the user is NOT authenticated — tags Clarity with the anonymous
// session UUID (used as customId) and stamps it as a Clarity custom attribute
// so recordings remain reachable even if a mid-session sign-up re-binds the ID.
export function identifyClarityAnonymous(pathname: string): void {
  const distinctId = getDistinctId();
  const sessionId = getOrCreateClaritySessionId();
  if (distinctId) claritySet("mp_distinct_id", distinctId);
  claritySet("anon_session_id", sessionId);
  clarityIdentify(sessionId, sessionId, pathname);
}

// Ensures Clarity is identified and identity super-props are on Mixpanel.
// Called by track() on every event — the common case is a fast session-key check.
export function ensureClarityFresh(userId: string | null, pathname: string): void {
  if (!userId) {
    if (!sessionStorage.getItem(CLARITY_SESSION_KEY)) {
      const sessionId = getOrCreateClaritySessionId();
      identifyClarityAnonymous(pathname);
      registerSuperProperties({
        clarity_session_id: sessionId,
        clarity_visitor_type: "anonymous",
      });
    }
    return;
  }

  if (!sessionStorage.getItem(CLARITY_SESSION_KEY)) {
    syncClarityIntoMixpanel(userId, pathname);
  }
}

// Called on logout — clears Clarity identity super-props from Mixpanel localStorage
// and removes the session bucket from sessionStorage.
// Must be called before any redirect so the write completes before page unload.
export function clearClarityMixpanelProps(): void {
  try {
    sessionStorage.removeItem(CLARITY_SESSION_KEY);
  } catch {
    // ignore
  }
  unregisterSuperProperties(...CLARITY_SUPER_PROPS);
}

// Single entry point for every product event.
// Computes a per-event Clarity recording URL centered on the event's UTC timestamp
// so the Clarity dashboard opens to the correct recording slice regardless of how
// long the session has been running.
export function track(event: string, properties?: Record<string, any>): void {
  if (typeof window === "undefined") return;
  ensureClarityFresh(getCurrentUserId(), currentPathname());
  clarityEvent(event);

  const now = Date.now();
  const userId = getCurrentUserId();
  const customUserId = userId ?? sessionStorage.getItem(CLARITY_SESSION_KEY) ?? "";
  const clarityRecordingUrl = buildRecordingUrl(customUserId, now - WINDOW_BEFORE_MS, now + WINDOW_AFTER_MS);

  trackEvent(event, {
    ...properties,
    ...(clarityRecordingUrl
      ? { clarity_recording_url: clarityRecordingUrl, clarity_event_timestamp: now }
      : {}),
  });
}
